"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { auth } from "../api/firebase-ultra-safe";
import { onAuthStateChanged, User as FirebaseUser } from "firebase/auth";
import { getUserProfile } from "../profile/api";
import { User } from "../../data/types";
import { UserProfile } from "../../data/profile/types";
import { useSession } from "next-auth/react";
import {
  ensureFirebaseAuthFromNextAuth,
  resolveNextAuthUserId,
} from "../auth/ensureFirebaseAuthFromNextAuth";

// UserProfile을 User로 변환하는 함수
function convertUserProfileToUser(
  profile: UserProfile,
  firebaseUser?: FirebaseUser
): User {
  return {
    id: profile.uid,
    uid: profile.uid,
    email: profile.email || firebaseUser?.email || "",
    phoneNumber: profile.phoneNumber,
    nickname: profile.nickname || firebaseUser?.displayName || "사용자",
    region: profile.region || "지역 정보 없음",
    grade: profile.grade || "C",
    tradeCount: profile.tradesCount || 0,
    reviewCount: profile.reviewsCount || 0,
    createdAt: profile.createdAt?.toDate() || new Date(),
    updatedAt: profile.updatedAt?.toDate() || new Date(),
    profileImage: profile.photoURL || firebaseUser?.photoURL || undefined,
    safeTransactionCount: 0,
    averageRating: 0,
    disputeCount: 0,
    isPhoneVerified: profile.phoneVerified || false,
  };
}

// 전역 상태 관리
let globalUser: User | null = null;
let globalLoading = true;
const globalListeners: Set<() => void> = new Set();

function notifyListeners() {
  globalListeners.forEach(listener => listener());
}

// Firebase 사용자 처리 함수
async function processFirebaseUser(
  firebaseUser: FirebaseUser | null
): Promise<User | null> {
  if (!firebaseUser) {
    return null;
  }

  try {
    const userData = await getUserProfile(firebaseUser.uid);
    if (userData && userData.success && userData.data) {
      return convertUserProfileToUser(userData.data, firebaseUser);
    } else {
      return {
        id: firebaseUser.uid,
        uid: firebaseUser.uid,
        email: firebaseUser.email || "",
        phoneNumber: undefined,
        nickname:
          firebaseUser.displayName ||
          firebaseUser.email?.split("@")[0] ||
          "사용자",
        region: "지역 정보 없음",
        grade: "C",
        tradeCount: 0,
        reviewCount: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        profileImage: firebaseUser.photoURL || undefined,
        safeTransactionCount: 0,
        averageRating: 0,
        disputeCount: 0,
        isPhoneVerified: false,
      };
    }
  } catch (error) {
    console.error("사용자 정보 가져오기 실패:", error);
    return {
      id: firebaseUser.uid,
      uid: firebaseUser.uid,
      email: firebaseUser.email || "",
      phoneNumber: undefined,
      nickname: firebaseUser.displayName || "사용자",
      region: "",
      grade: "C",
      tradeCount: 0,
      reviewCount: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
      profileImage: firebaseUser.photoURL || undefined,
      safeTransactionCount: 0,
      averageRating: 0,
      disputeCount: 0,
      isPhoneVerified: false,
    };
  }
}

// Firebase Auth 초기화
let isInitialized = false;
let unsubscribe: (() => void) | null = null;
let firebaseAuthReady = false;

function initializeAuth() {
  if (isInitialized || !auth) {
    return;
  }

  isInitialized = true;
  globalLoading = true;
  console.log("🔥 useAuth: Firebase Auth 초기화");

  unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
    console.log(
      "🔥 useAuth: Firebase Auth 상태 변경:",
      firebaseUser?.uid || "null"
    );

    if (firebaseUser) {
      const user = await processFirebaseUser(firebaseUser);
      globalUser = user;
    } else {
      globalUser = null;
    }

    firebaseAuthReady = true;
    globalLoading = false;
    notifyListeners();
  });
}

function cleanup() {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  isInitialized = false;
  firebaseAuthReady = false;
  globalUser = null;
  globalLoading = true;
  globalListeners.clear();
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(globalUser);
  const [loading, setLoading] = useState(globalLoading);
  const listenerRef = useRef<() => void>();
  const { data: session, status: sessionStatus } = useSession();

  // 개발용 더미 사용자 확인
  useEffect(() => {
    if (typeof window !== "undefined" && !globalUser) {
      const devDummyUser = localStorage.getItem("dev-dummy-user");
      if (devDummyUser) {
        try {
          const dummyUser = JSON.parse(devDummyUser);
          console.log("🧪 개발용 더미 사용자 로드됨:", dummyUser);
          setUser(dummyUser);
          setLoading(false);
          globalUser = dummyUser;
          globalLoading = false;
          globalListeners.forEach(listener => listener());
        } catch (error) {
          console.error("❌ 더미 사용자 파싱 실패:", error);
          localStorage.removeItem("dev-dummy-user");
        }
      }
    }
  }, []);

  // 리스너 등록/해제
  useEffect(() => {
    initializeAuth();

    listenerRef.current = () => {
      setUser(globalUser);
      setLoading(globalLoading);
    };

    globalListeners.add(listenerRef.current);
    setUser(globalUser);
    setLoading(globalLoading);

    return () => {
      if (listenerRef.current) {
        globalListeners.delete(listenerRef.current);
      }
    };
  }, []);

  // NextAuth 세션 처리
  useEffect(() => {
    const handleNextAuthSession = async () => {
      if (sessionStatus === "loading") {
        return;
      }

      if (session?.user && !globalUser) {
        try {
          const uid = resolveNextAuthUserId(session);
          if (!uid) return;

          await ensureFirebaseAuthFromNextAuth(uid);

          const profileResult = await getUserProfile(uid);
          if (profileResult.success && profileResult.data) {
            globalUser = convertUserProfileToUser(profileResult.data);
            globalLoading = false;
            notifyListeners();
          }
        } catch (error) {
          console.error("❌ NextAuth 사용자 프로필 로드 실패:", error);
        }
      }
    };

    handleNextAuthSession();
  }, [session, sessionStatus]);

  // 사용자 정보 새로고침 함수
  const refreshUser = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const userData = await getUserProfile(user.uid);
      if (userData && userData.success && userData.data && auth.currentUser) {
        const updatedUser = convertUserProfileToUser(
          userData.data,
          auth.currentUser
        );
        globalUser = updatedUser;
        notifyListeners();
      }
    } catch (error) {
      console.error("사용자 정보 새로고침 실패:", error);
    }
  }, [user?.uid]);

  // 사용자 정보 업데이트 함수
  const updateUser = useCallback((updatedUserData: Partial<User>) => {
    if (globalUser) {
      globalUser = { ...globalUser, ...updatedUserData };
      notifyListeners();
    }
  }, []);

  // 로그아웃 함수
  const logout = useCallback(async () => {
    try {
      await auth.signOut();
      globalUser = null;
      globalLoading = false;
      notifyListeners();
    } catch (error) {
      console.error("로그아웃 실패:", error);
    }
  }, []);

  return {
    user,
    loading: loading || sessionStatus === "loading" || !firebaseAuthReady,
    isAuthenticated: !!user || !!session?.user,
    refreshUser,
    logout,
    updateUser,
  };
}

// 개발 환경에서만 전역 정리 함수 노출
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  (window as unknown as Record<string, unknown>).__cleanupAuth = cleanup;
}
